import PropTypes from 'prop-types';
import '@/styles/PageHeader.scss';

/**
 * PageHeader
 *
 * Shared layout contract for every settings/designer page so that navigating
 * between them feels consistent: title + description on the left, primary
 * and secondary actions on the right, an optional status/meta slot, and an
 * optional toolbar row below (filters, tabs, search).
 *
 * Adopting this header across designers is the single cheapest UX win for
 * reducing drift between pages built at different times.
 *
 * Usage:
 *
 *     <PageHeader
 *         title="Email Templates"
 *         description="Design branded templates used by scheduled reports and lifecycle notifications."
 *         primaryAction={<button onClick={onNew}>New Template</button>}
 *         meta={<StatusPill tone="info">{templates.length} active</StatusPill>}
 *         toolbar={<SearchInput ... />}
 *     />
 */
const PageHeader = ({
    title,
    description,
    icon = null,
    primaryAction = null,
    secondaryActions = null,
    meta = null,
    toolbar = null,
    className = ''
}) => {
    return (
        <header className={`ctx-page-header${className ? ` ${className}` : ''}`}>
            <div className="ctx-page-header-row">
                <div className="ctx-page-header-left">
                    {icon && <span className="ctx-page-header-icon" aria-hidden="true">{icon}</span>}
                    <div className="ctx-page-header-text">
                        <h1 className="ctx-page-header-title">{title}</h1>
                        {description && <p className="ctx-page-header-description">{description}</p>}
                    </div>
                </div>
                <div className="ctx-page-header-right">
                    {meta && <div className="ctx-page-header-meta">{meta}</div>}
                    {secondaryActions && <div className="ctx-page-header-secondary">{secondaryActions}</div>}
                    {primaryAction && <div className="ctx-page-header-primary">{primaryAction}</div>}
                </div>
            </div>
            {toolbar && <div className="ctx-page-header-toolbar">{toolbar}</div>}
        </header>
    );
};

PageHeader.propTypes = {
    title: PropTypes.node.isRequired,
    description: PropTypes.node,
    icon: PropTypes.node,
    primaryAction: PropTypes.node,
    secondaryActions: PropTypes.node,
    meta: PropTypes.node,
    toolbar: PropTypes.node,
    className: PropTypes.string
};

export default PageHeader;
